/**
 * CpaSafeguardAttributionFraudProtectionPage — CPA attribution fraud safeguard dashboard.
 *
 * Two cards:
 * 1) Rules — create + list + soft-delete (POST/GET/DELETE /rules).
 * 2) Verdicts — latest fraud check verdicts (GET /verdicts) with flag chips.
 *
 * Brand: emerald -> sky gradient headlines, zinc borders, 6-8px rounding.
 */
import { useState, type FormEvent } from "react";
import type { Action, RuleType, Verdict } from "./api";
import {
	useCpaSafeguardAttributionFraudProtectionRulesList,
	useCpaSafeguardAttributionFraudProtectionVerdictsList,
	useCpaSafeguardAttributionFraudProtectionCreateRule,
	useCpaSafeguardAttributionFraudProtectionDeleteRule,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";

const RULE_TYPES: RuleType[] = [
	"click_window_max",
	"click_window_min",
	"country_mismatch",
	"ip_mismatch",
	"device_mismatch",
	"affiliate_velocity",
	"self_referral",
	"payout_cap",
];
const ACTIONS: Action[] = ["approve", "review", "reject"];

function verdictTone(v: Verdict): string {
	switch (v) {
 case "approve":
 return "bg-green-50 text-green-700 border-green-200";
 case "review":
 return "bg-green-50 text-green-700 border-green-200";
 case "reject":
 return "bg-red-50 text-red-700 border-red-200";
	}
}

function fmtCents(cents: number): string {
	const n = Math.max(0, Math.floor(cents));
	return `$${(n / 100).toFixed(2)}`;
}

export function CpaSafeguardAttributionFraudProtectionPage() {
	const rulesQuery = useCpaSafeguardAttributionFraudProtectionRulesList();
	const verdictsQuery = useCpaSafeguardAttributionFraudProtectionVerdictsList();
	const createRuleMut = useCpaSafeguardAttributionFraudProtectionCreateRule();
	const deleteRuleMut = useCpaSafeguardAttributionFraudProtectionDeleteRule();

	const [ruleType, setRuleType] = useState<RuleType>("click_window_max");
	const [pattern, setPattern] = useState("");
	const [action, setAction] = useState<Action>("review");

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 const p = pattern.trim();
 if (!p) return;
 createRuleMut.mutate(
 { rule_type: ruleType, pattern: p, action },
 { onSuccess: () => setPattern("") },
 );
	};

	const rules = rulesQuery.data?.rules ?? [];
	const verdicts = verdictsQuery.data?.verdicts ?? [];

	return (
 <main
 data-testid="cpa-safeguard-attribution-fraud-protection-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 cpa-safeguard-attribution-fraud-protection
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 CPA Attribution Fraud Safeguard
 </h1>
 <p className="text-sm text-zinc-600">
 Score CPA conversions against safeguard rules. Reject clears the
 payout; review holds it for manual release.
 </p>
 </header>

 <section
 className={CARD}
 aria-label="Create safeguard rule"
 >
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Create safeguard rule
 </h2>
 <form
 onSubmit={handleCreate}
 className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4"
 data-testid="cpa-safeguard-attribution-fraud-protection-create-form"
 >
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
 Rule type
 <select
 className={SELECT}
 value={ruleType}
 onChange={(e) => setRuleType(e.currentTarget.value as RuleType)}
 disabled={createRuleMut.isPending}
 data-testid="cpa-safeguard-attribution-fraud-protection-rule-type"
 >
 {RULE_TYPES.map((t) => (
 <option key={t} value={t}>
 {t}
 </option>
 ))}
 </select>
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
 Pattern
 <input
 type="text"
 className={INPUT}
 placeholder="e.g. 3600 or * or US,CA"
 value={pattern}
 onChange={(e) => setPattern(e.currentTarget.value)}
 disabled={createRuleMut.isPending}
 data-testid="cpa-safeguard-attribution-fraud-protection-pattern"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
 Action
 <select
 className={SELECT}
 value={action}
 onChange={(e) => setAction(e.currentTarget.value as Action)}
 disabled={createRuleMut.isPending}
 data-testid="cpa-safeguard-attribution-fraud-protection-action"
 >
 {ACTIONS.map((a) => (
 <option key={a} value={a}>
 {a}
 </option>
 ))}
 </select>
 </label>
 <div className="flex items-end">
 <button
 type="submit"
 className={BTN}
 disabled={createRuleMut.isPending || !pattern.trim()}
 data-testid="cpa-safeguard-attribution-fraud-protection-submit"
 >
 {createRuleMut.isPending ? "Saving." : "Create rule"}
 </button>
 </div>
 </form>
 {createRuleMut.error ? (
 <p className="mt-2 text-xs text-red-600" role="alert">
 {createRuleMut.error.message}
 </p>
 ) : null}
 </section>

 <section
 className={CARD}
 aria-label="Safeguard rules list"
 >
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Active rules</h2>
 {rulesQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cpa-safeguard-attribution-fraud-protection-rules-loading"
 >
 Loading.
 </p>
 ) : rulesQuery.error ? (
 <p className="mt-3 text-sm text-red-600" role="alert">
 {rulesQuery.error.message}
 </p>
 ) : rules.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cpa-safeguard-attribution-fraud-protection-rules-empty"
 >
 No rules yet. Create one above.
 </p>
 ) : (
 <ul
 className="mt-3 divide-y divide-zinc-200"
 data-testid="cpa-safeguard-attribution-fraud-protection-rules-list"
 >
 {rules.map((r) => (
 <li
 key={r.id}
 className="flex items-center justify-between py-2 text-sm"
 data-testid={`cpa-safeguard-attribution-fraud-protection-rule-${r.id}`}
 >
 <div className="flex flex-1 flex-col gap-0.5">
 <span className="font-mono text-xs text-zinc-900">
 {r.rule_type}
 </span>
 <span className="text-xs text-zinc-500">
 pattern: <span className="font-mono">{r.pattern}</span> ·{""}
 action:{""}
 <span className="font-mono">{r.action}</span>
 {r.active ? null :" · inactive"}
 </span>
 </div>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() => deleteRuleMut.mutate(r.id)}
 disabled={deleteRuleMut.isPending}
 data-testid={`cpa-safeguard-attribution-fraud-protection-delete-${r.id}`}
 >
 Delete
 </button>
 </li>
 ))}
 </ul>
 )}
 </section>

 <section
 className={CARD}
 aria-label="Recent verdicts"
 >
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Recent verdicts
 </h2>
 {verdictsQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cpa-safeguard-attribution-fraud-protection-verdicts-loading"
 >
 Loading.
 </p>
 ) : verdictsQuery.error ? (
 <p className="mt-3 text-sm text-red-600" role="alert">
 {verdictsQuery.error.message}
 </p>
 ) : verdicts.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="cpa-safeguard-attribution-fraud-protection-verdicts-empty"
 >
 No verdicts yet. Conversions checked via the API will appear here.
 </p>
 ) : (
 <ul
 className="mt-3 divide-y divide-zinc-200"
 data-testid="cpa-safeguard-attribution-fraud-protection-verdicts-list"
 >
 {verdicts.map((v) => (
 <li
 key={`${v.conversion_id}-${v.created_at}`}
 className="flex flex-col gap-1 py-2 text-sm"
 data-testid={`cpa-safeguard-attribution-fraud-protection-verdict-${v.conversion_id}`}
 >
 <div className="flex items-center justify-between">
 <span className="font-mono text-xs text-zinc-900">
 {v.conversion_id}
 </span>
 <span
 className={`rounded-[6px] border px-2 py-0.5 text-xs font-medium ${verdictTone(
 v.verdict as Verdict,
 )}`}
 >
 {v.verdict}
 </span>
 </div>
 <div className="flex flex-wrap gap-2 text-xs text-zinc-600">
 <span>
 risk: <span className="font-mono">{v.risk_score}</span>
 </span>
 <span>
 payout:{""}
 <span className="font-mono">
 {fmtCents(v.payout_amount_cents)}
 </span>
 </span>
 <span>
 withheld:{""}
 <span className="font-mono">
 {fmtCents(v.withheld_cents)}
 </span>
 </span>
 {v.affiliate_id ? (
 <span>
 affiliate:{""}
 <span className="font-mono">{v.affiliate_id}</span>
 </span>
 ) : null}
 </div>
 {v.flags.length > 0 ? (
 <div className="flex flex-wrap gap-1">
 {v.flags.map((f) => (
 <span
 key={f}
 className="rounded-[6px] border border-green-200 bg-green-50 px-1.5 py-0.5 font-mono text-[11px] text-green-700"
 >
 {f}
 </span>
 ))}
 </div>
 ) : null}
 </li>
 ))}
 </ul>
 )}
 </section>
 </main>
	);
}

export default CpaSafeguardAttributionFraudProtectionPage;
